
import { useState } from 'react';
import { useNodes, useUpdateNode, useDeleteNode } from '@/hooks/useNodes';
import { useDisciplines } from '@/hooks/useDisciplines';
import { useToast } from '@/hooks/use-toast';

export const useContentManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [disciplineFilter, setDisciplineFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  const { data: allNodes, isLoading } = useNodes();
  const { data: disciplines } = useDisciplines();
  const { mutate: updateNode } = useUpdateNode();
  const { mutate: deleteNode } = useDeleteNode();
  const { toast } = useToast();

  // Filter nodes based on search and filters
  const filteredNodes = allNodes?.filter(node => {
    const matchesSearch = node.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || statusFilter === 'all' || node.status === statusFilter;
    const matchesDiscipline = !disciplineFilter || disciplineFilter === 'all' || node.discipline_id === disciplineFilter;
    const matchesType = !typeFilter || typeFilter === 'all' || node.node_type === typeFilter;
    
    return matchesSearch && matchesStatus && matchesDiscipline && matchesType;
  });

  const handleFeatureToggle = (nodeId: string, currentFeatured: boolean) => {
    updateNode(
      { id: nodeId, updates: { featured: !currentFeatured } },
      {
        onSuccess: () => {
          toast({
            title: "Content updated",
            description: `Content ${!currentFeatured ? 'featured' : 'unfeatured'} successfully.`,
          });
        },
        onError: () => {
          toast({
            title: "Error",
            description: "Failed to update content.",
            variant: "destructive",
          });
        },
      }
    );
  };

  const handleStatusChange = (nodeId: string, newStatus: string) => {
    updateNode(
      { id: nodeId, updates: { status: newStatus } },
      {
        onSuccess: () => {
          toast({
            title: "Status updated",
            description: `Content status changed to ${newStatus}.`,
          });
        },
        onError: () => {
          toast({
            title: "Error",
            description: "Failed to update status.",
            variant: "destructive",
          });
        },
      }
    );
  };

  const handleDelete = (nodeId: string, title: string) => {
    if (window.confirm(`Are you sure you want to delete "${title}"? This action cannot be undone.`)) {
      deleteNode(nodeId, {
        onSuccess: () => {
          toast({
            title: "Content deleted",
            description: "Content has been permanently deleted.",
          });
        },
        onError: () => {
          toast({
            title: "Error",
            description: "Failed to delete content.",
            variant: "destructive",
          });
        },
      });
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setDisciplineFilter('all');
    setTypeFilter('all');
  };

  return {
    // State
    searchTerm,
    statusFilter,
    disciplineFilter,
    typeFilter,
    
    // Data
    allNodes,
    filteredNodes,
    disciplines,
    isLoading,
    
    // Actions
    setSearchTerm,
    setStatusFilter,
    setDisciplineFilter,
    setTypeFilter,
    handleFeatureToggle,
    handleStatusChange,
    handleDelete,
    handleClearFilters,
  };
};
